import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  CONSENT_FIELD_LIMITS,
  CONSENT_FIELD_TYPES,
  addConsentField,
  consentFieldDataKey,
  moveConsentField,
  optionsText,
  parseOptionsText,
  removeConsentField,
  updateConsentField,
} from "./consentSpecificFields";

/** The `source` of a dropdown whose options the author types. */
const CUSTOM_LIST = "";

const ConsentFieldsEditor = ({
  fields = [],
  sources = [],
  presets = [],
  disabled = false,
  onChange,
}) => {
  // The options text as it is being typed, per row — the stored list is only
  // what parses, which would eat the newline or comma just typed.
  const [drafts, setDrafts] = useState({});

  const change = (next) => onChange?.(next);
  const full = fields.length >= CONSENT_FIELD_LIMITS.fieldsPerTemplate;
  const missingPresets = presets.filter(
    (preset) => !fields.some((field) => field.key === preset.key)
  );

  return (
    <Box className="consent-fields-editor" data-testid="consent-fields-editor">
      {fields.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#78808f", my: 1 }}>
          No fields. The patient, doctor and clinic details are always printed; add a field only
          for what this consent itself needs (anaesthesia, investigation, reason for referral…).
          Every field is optional when the consent is recorded.
        </Typography>
      ) : null}

      {fields.map((field, index) => {
        const isSelect = field.type === "select";
        const optionsDraft = drafts[index] ?? optionsText(field.options);

        return (
          <Box className="consent-field-row" key={index} data-testid={`consent-field-row-${index}`}>
            <TextField
              size="small"
              label="Field label"
              value={field.label || ""}
              disabled={disabled}
              inputProps={{
                maxLength: CONSENT_FIELD_LIMITS.labelMax,
                "data-testid": `consent-field-label-${index}`,
              }}
              onChange={(event) =>
                change(updateConsentField(fields, index, { label: event.target.value }))
              }
            />
            <TextField
              select
              size="small"
              label="Type"
              value={isSelect ? "select" : "text"}
              disabled={disabled}
              inputProps={{ "data-testid": `consent-field-type-${index}` }}
              onChange={(event) =>
                change(updateConsentField(fields, index, { type: event.target.value }))
              }
            >
              {CONSENT_FIELD_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            {isSelect ? (
              <TextField
                select
                size="small"
                label="Options from"
                value={field.source || CUSTOM_LIST}
                disabled={disabled}
                inputProps={{ "data-testid": `consent-field-source-${index}` }}
                onChange={(event) =>
                  change(updateConsentField(fields, index, { source: event.target.value }))
                }
              >
                <MenuItem value={CUSTOM_LIST}>Custom list (typed below)</MenuItem>
                {sources.map((source) => (
                  <MenuItem key={source.key} value={source.key}>
                    {source.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box />
            )}
            <Stack direction="row" className="consent-field-row__actions">
              <Tooltip title="Move up">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Move ${field.label || "field"} up`}
                    disabled={disabled || index === 0}
                    onClick={() => change(moveConsentField(fields, index, -1))}
                  >
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Move ${field.label || "field"} down`}
                    disabled={disabled || index === fields.length - 1}
                    onClick={() => change(moveConsentField(fields, index, 1))}
                  >
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Remove ${field.label || "field"}`}
                    disabled={disabled}
                    onClick={() => change(removeConsentField(fields, index))}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            {isSelect && !field.source ? (
              <TextField
                size="small"
                className="consent-field-row__options"
                label="Options (one per line, or comma-separated)"
                multiline
                minRows={2}
                value={optionsDraft}
                disabled={disabled}
                inputProps={{ "data-testid": `consent-field-options-${index}` }}
                onChange={(event) => {
                  const text = event.target.value;
                  setDrafts((current) => ({ ...current, [index]: text }));
                  change(updateConsentField(fields, index, { options: parseOptionsText(text) }));
                }}
                onBlur={() =>
                  setDrafts((current) => {
                    const next = { ...current };
                    delete next[index];
                    return next;
                  })
                }
              />
            ) : null}

            {/* <Typography
              variant="caption"
              className="consent-field-row__hint"
              sx={{ color: "#78808f" }}
            >
              Prints as &ldquo;{field.label || "…"}: value&rdquo; under the title. Placeholder for
              the content: <code>{`{{${consentFieldDataKey(field.key)}}}`}</code>
            </Typography> */}
          </Box>
        );
      })}

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={disabled || full}
          data-testid="consent-field-add"
          onClick={() => change(addConsentField(fields))}
        >
          Add field
        </Button>
        {missingPresets.length ? (
          <Button
            size="small"
            disabled={disabled || full}
            data-testid="consent-field-presets"
            onClick={() => change([...fields, ...missingPresets])}
          >
            Add the suggested fields for this format
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ConsentFieldsEditor;
